import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState, useCallback } from "react";
import { cn } from "@/lib/utils";
import { X, Loader2 } from "lucide-react";
import {
  loadPlacesLibrary,
  loadMapsLibrary,
  loadMarkerLibrary,
  isPlacesLoaded,
  isMapsLoaded,
  isMarkerLoaded,
  hasApiKey,
} from "@/lib/google-maps";

export interface AddressData {
  formattedAddress: string;
  lat: number;
  lng: number;
  placeId: string;
}

interface AddressAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onSelect: (data: AddressData | null) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  id?: string;
  showMap?: boolean;
  selectedAddress?: AddressData | null;
  mapClassName?: string;
}

interface Prediction {
  placeId: string;
  mainText: string;
  secondaryText: string;
  fullText: string;
}

/**
 * Custom Google Places Autocomplete component using the Data API.
 * This gives full control over styling while using Google's autocomplete predictions.
 * Restricted to Netherlands addresses.
 */
export function AddressAutocomplete({
  value,
  onChange,
  onSelect,
  placeholder,
  disabled = false,
  className,
  id,
  showMap = true,
  selectedAddress,
  mapClassName,
}: AddressAutocompleteProps) {
  const { t } = useTranslation("common");
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const markerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null);
  const autocompleteServiceRef = useRef<google.maps.places.AutocompleteService | null>(null);
  const placesServiceRef = useRef<google.maps.places.PlacesService | null>(null);
  const sessionTokenRef = useRef<google.maps.places.AutocompleteSessionToken | null>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const [isLoaded, setIsLoaded] = useState(isPlacesLoaded());
  const [error, setError] = useState<string | null>(null);
  const [predictions, setPredictions] = useState<Prediction[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [mapLoaded, setMapLoaded] = useState(isMapsLoaded() && isMarkerLoaded());
  const [internalSelectedAddress, setInternalSelectedAddress] = useState<AddressData | null>(null);

  // Use either external selectedAddress prop or internal state
  const currentSelectedAddress = selectedAddress !== undefined ? selectedAddress : internalSelectedAddress;

  const initServices = useCallback(() => {
    if (!autocompleteServiceRef.current) {
      autocompleteServiceRef.current = new google.maps.places.AutocompleteService();
    }
    // Create a dummy div for PlacesService (required but not visible)
    if (!placesServiceRef.current) {
      const dummyDiv = document.createElement("div");
      placesServiceRef.current = new google.maps.places.PlacesService(dummyDiv);
    }
    // Create new session token
    if (!sessionTokenRef.current) {
      sessionTokenRef.current = new google.maps.places.AutocompleteSessionToken();
    }
    setIsLoaded(true);
  }, []);

  // Initialize Google Maps Places library
  useEffect(() => {
    if (!hasApiKey()) {
      setError("Google Maps API key niet geconfigureerd");
      return;
    }

    if (isPlacesLoaded()) {
      initServices();
      return;
    }

    const init = async () => {
      try {
        await loadPlacesLibrary();
        initServices();
        setError(null);
      } catch (err) {
        console.error("Failed to load Google Maps:", err);
        setError("Kon Google Maps niet laden");
      }
    };

    init();
  }, [initServices]);

  // Load maps library for displaying the map
  useEffect(() => {
    if (!showMap || !isLoaded) return;

    if (isMapsLoaded() && isMarkerLoaded()) {
      setMapLoaded(true);
      return;
    }

    const loadMaps = async () => {
      try {
        await Promise.all([
          loadMapsLibrary(),
          loadMarkerLibrary(),
        ]);
        setMapLoaded(true);
      } catch (err) {
        console.error("Failed to load Google Maps:", err);
      }
    };

    loadMaps();
  }, [showMap, isLoaded]);

  // Initialize or update map when address is selected
  useEffect(() => {
    if (!showMap || !mapLoaded || !currentSelectedAddress || !mapContainerRef.current) {
      return;
    }

    const { lat, lng } = currentSelectedAddress;
    const position = { lat, lng };

    // Initialize map if not already created
    if (!mapRef.current) {
      mapRef.current = new google.maps.Map(mapContainerRef.current, {
        center: position,
        zoom: 16,
        mapId: "address-autocomplete-map",
        disableDefaultUI: true,
        zoomControl: true,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
      });
    } else {
      // Update existing map center
      mapRef.current.setCenter(position);
    }

    // Create or update marker
    if (!markerRef.current) {
      markerRef.current = new google.maps.marker.AdvancedMarkerElement({
        map: mapRef.current,
        position,
        title: currentSelectedAddress.formattedAddress,
      });
    } else {
      markerRef.current.position = position;
      markerRef.current.title = currentSelectedAddress.formattedAddress;
    }
  }, [showMap, mapLoaded, currentSelectedAddress]);

  // Fetch predictions from Google
  const fetchPredictions = useCallback(async (input: string) => {
    if (!input.trim() || !autocompleteServiceRef.current) {
      setPredictions([]);
      setIsOpen(false);
      return;
    }

    setIsSearching(true);

    try {
      // Utrecht bounding box (roughly)
      const utrechtBounds = new google.maps.LatLngBounds(
        new google.maps.LatLng(52.04, 5.03), // Southwest corner
        new google.maps.LatLng(52.14, 5.18)  // Northeast corner
      );

      const response = await autocompleteServiceRef.current.getPlacePredictions({
        input,
        componentRestrictions: { country: "nl" },
        types: ["address"],
        locationBias: utrechtBounds,
        language: "nl",
        sessionToken: sessionTokenRef.current || undefined,
      });

      const newPredictions: Prediction[] = (response.predictions || []).map((p) => ({
        placeId: p.place_id,
        mainText: p.structured_formatting.main_text,
        secondaryText: p.structured_formatting.secondary_text || "",
        fullText: p.description,
      }));

      setPredictions(newPredictions);
      setIsOpen(newPredictions.length > 0);
      setSelectedIndex(-1);
    } catch (err) {
      console.error("Autocomplete error:", err);
      setPredictions([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Handle input change with debounce
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      onChange(newValue);
      onSelect(null); // Clear selection when typing

      // Debounce the API call
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(() => {
        fetchPredictions(newValue);
      }, 300);
    },
    [onChange, onSelect, fetchPredictions]
  );

  // Handle prediction selection
  const handleSelectPrediction = useCallback(
    async (prediction: Prediction) => {
      if (!placesServiceRef.current) return;

      onChange(prediction.fullText);
      setPredictions([]);
      setIsOpen(false);

      // Fetch place details to get coordinates
      placesServiceRef.current.getDetails(
        {
          placeId: prediction.placeId,
          fields: ["formatted_address", "geometry"],
          sessionToken: sessionTokenRef.current || undefined,
        },
        (place, status) => {
          if (status === google.maps.places.PlacesServiceStatus.OK && place) {
            const addressData: AddressData = {
              formattedAddress: place.formatted_address || prediction.fullText,
              lat: place.geometry?.location?.lat() || 0,
              lng: place.geometry?.location?.lng() || 0,
              placeId: prediction.placeId,
            };
            setInternalSelectedAddress(addressData);
            onSelect(addressData);

            // Create new session token for next search
            sessionTokenRef.current = new google.maps.places.AutocompleteSessionToken();
          }
        }
      );
    },
    [onChange, onSelect]
  );

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!isOpen || predictions.length === 0) {
        if (e.key === "Escape") {
          setIsOpen(false);
        }
        return;
      }

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setSelectedIndex((prev) =>
            prev < predictions.length - 1 ? prev + 1 : prev
          );
          break;
        case "ArrowUp":
          e.preventDefault();
          setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
          break;
        case "Enter":
          e.preventDefault();
          if (selectedIndex >= 0 && predictions[selectedIndex]) {
            handleSelectPrediction(predictions[selectedIndex]);
          }
          break;
        case "Escape":
          e.preventDefault();
          setIsOpen(false);
          setSelectedIndex(-1);
          break;
      }
    },
    [isOpen, predictions, selectedIndex, handleSelectPrediction]
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Cleanup debounce timer
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Clear the input
  const handleClear = useCallback(() => {
    onChange("");
    onSelect(null);
    setInternalSelectedAddress(null);
    setPredictions([]);
    setIsOpen(false);
    inputRef.current?.focus();
  }, [onChange, onSelect]);

  if (error) {
    return (
      <div className={cn("relative", className)}>
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={t("placeholders.address")}
          disabled={disabled}
          id={id}
          className={cn(
            "flex h-16 w-full border border-input bg-background px-3 py-2 pl-10 text-base",
            "placeholder:text-muted-foreground",
            "focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:border-ring",
            "disabled:cursor-not-allowed disabled:opacity-50",
            "transition-[color,box-shadow]"
          )}
        />
        <p className="text-sm text-destructive mt-1">{error}</p>
      </div>
    );
  }

  return (
    <div className={cn("flex flex-col", className)}>
      {/* Input wrapper with relative positioning for dropdown */}
      <div className="relative flex-shrink-0">
        {/* Input field */}
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (predictions.length > 0) setIsOpen(true);
          }}
          placeholder={placeholder ?? t("placeholders.addressSearch")}
          disabled={disabled || !isLoaded}
          id={id}
          autoComplete="off"
          role="combobox"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-autocomplete="list"
          className={cn(
            "flex h-16 w-full border border-input bg-background px-3 py-2 text-base",
            "placeholder:text-muted-foreground",
            "focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:border-ring",
            "disabled:cursor-not-allowed disabled:opacity-50",
            "transition-[color,box-shadow]"
          )}
        />

        {/* Loading indicator or clear button */}
        {isSearching ? (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground animate-spin" />
        ) : value && !disabled && isLoaded ? (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Wis adres"
          >
            <X className="h-4 w-4" />
          </button>
        ) : null}

        {/* Loading state */}
        {!isLoaded && (
          <div className="absolute inset-0 flex items-center justify-center bg-background/50">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        )}

        {/* Predictions dropdown */}
        {isOpen && predictions.length > 0 && (
          <div
            ref={dropdownRef}
            role="listbox"
            className="absolute top-full left-0 right-0 z-50 mt-1 max-h-60 overflow-auto border border-input bg-background shadow-lg"
          >
            {predictions.map((prediction, index) => (
              <button
                key={prediction.placeId}
                type="button"
                role="option"
                aria-selected={index === selectedIndex}
                onClick={() => handleSelectPrediction(prediction)}
                onMouseEnter={() => setSelectedIndex(index)}
                className={cn(
                  "w-full px-3 py-3 text-left text-sm transition-colors",
                  "hover:bg-accent focus:bg-accent focus:outline-none",
                  index === selectedIndex && "bg-accent"
                )}
              >
                <div className="font-medium text-foreground">
                  {prediction.mainText}
                </div>
                {prediction.secondaryText && (
                  <div className="text-muted-foreground text-xs mt-0.5">
                    {prediction.secondaryText}
                  </div>
                )}
              </button>
            ))}
            {/* Google attribution */}
            <div className="px-3 py-2 text-xs text-muted-foreground border-t border-input">
              Powered by Google
            </div>
          </div>
        )}
      </div>

      {/* Map display when address is selected */}
      {showMap && currentSelectedAddress && (
        <div className={cn("mt-3 flex-1 min-h-0", mapClassName)}>
          <div
            ref={mapContainerRef}
            className="w-full h-full border border-input bg-muted"
            aria-label={`Kaart van ${currentSelectedAddress.formattedAddress}`}
          />
        </div>
      )}
    </div>
  );
}
